import { gql, useQuery as useApolloQuery } from '@apollo/client';
import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DynamicPieChartWidget } from './DynamicPieChartWidget';
import { banqoraGraphQLEndpointPOST } from '~/api/def/endpoints/mcpGraphQLEndpoint';

export interface DynamicPieChartWidgetWrapperProps<
  T extends Record<string, unknown> = Record<string, unknown>,
> {
  query: string;
  widgetId: string;
  handleRemoveWidget?: (widgetId: string) => void;
  shortDescription?: string;
  isAggregate?: boolean;
  mapSlice: (item: T) => { name: string; value: number; currency?: string };
}

export const DynamicPieChartWidgetWrapper = <
  T extends Record<string, unknown> = Record<string, unknown>,
>({
  query,
  widgetId,
  handleRemoveWidget,
  shortDescription,
  isAggregate,
  mapSlice,
}: DynamicPieChartWidgetWrapperProps<T>) => {
  const dataQuery = gql`
    ${query}
  `;

  const isGQLMcp = isAggregate === true;
  const apolloQuery = useApolloQuery(dataQuery, {
    skip: isGQLMcp,
    notifyOnNetworkStatusChange: true,
  });
  const gqlMcpQuery = useQuery({
    queryKey: ['banqora-graphql', query],
    queryFn: () => banqoraGraphQLEndpointPOST({ query }),
    enabled: isGQLMcp,
  });

  const data = isGQLMcp ? gqlMcpQuery.data?.data : apolloQuery.data;
  const loading = isGQLMcp ? gqlMcpQuery.isLoading : apolloQuery.loading;
  const error = isGQLMcp ? gqlMcpQuery.error : apolloQuery.error;
  const refetch = isGQLMcp ? gqlMcpQuery.refetch : apolloQuery.refetch;
  const chartData = isGQLMcp
    ? data || []
    : data
      ? Object.values(data)[0] || []
      : [];

  const memoizedMapSlice = useCallback(
    (item: any) => {
      if (isGQLMcp && gqlMcpQuery.data?.query_info?.aggregations) {
        const aggregationField = Object.keys(
          gqlMcpQuery.data.query_info.aggregations,
        )[0];
        return {
          name: item.name || '',
          value: item[aggregationField] || 0,
        };
      }
      return mapSlice(item);
    },
    [isGQLMcp, gqlMcpQuery.data, mapSlice],
  );

  return (
    <DynamicPieChartWidget
      data={chartData}
      isLoading={loading}
      error={error ?? null}
      refetch={refetch}
      widgetId={widgetId}
      handleRemoveWidget={handleRemoveWidget}
      title={shortDescription}
      mapSlice={memoizedMapSlice}
    />
  );
};
